import React from "react";
import {
  Search2LineIconV7,
  Filter3LineIcon,
  SortDescIcon,
  ArrowDownSLineIconV7,
  ExpandUpDownFillIcon,
} from "../ui/figma-icons-v7";
import { DownloadPdfButton } from "../ui/download-pdf-button";
import { apiService } from "../../services/apiService";
import { Checkbox } from "./components/checkbox";
import { ColumnFiltersDropdown } from "./components/column-filters-dropdown";
import { SortDropdown } from "./components/sort-dropdown";
import { Pagination } from "./components/pagination";
import {
  InnerInterview,
  PriorCommunicationInfo,
  StatusTab,
  ColumnFilters,
  SortField,
} from "./types";
import { filterAndSortCandidates } from "./helpers";
import { InterviewStatusEnum } from "../../../generated-src/client/interviews/api";
import { InterviewResultEnum } from "../../../generated-src/client/interviews/api";
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from "../../hooks/useBrandingColors";

function formatPriorDate(iso: string): string {
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

const PriorCommunicationNotice: React.FC<{ info: PriorCommunicationInfo }> = ({ info }) => {
  const positionPart = [info.positionTitle, info.internalTag].filter(Boolean).join(' + ');
  const recruiterFio = [info.recruiterLastName, info.recruiterFirstName].filter(Boolean).join(' ');
  return (
    <div className="font-['Inter:Bold',_sans-serif] font-bold text-[14px] leading-[20px] text-[#b54708] bg-[#fef3c7] rounded-md px-2 py-1 mt-1 w-full whitespace-normal break-words">
      Кандидат уже общался на вакансию {positionPart || '—'}
      {recruiterFio ? `, ${recruiterFio}` : ''}
      {info.createdAt ? `, ${formatPriorDate(info.createdAt)}` : ''}
    </div>
  );
};

interface CandidatesTableProps {
  candidates: InnerInterview[];
  selectedPositionId: number | null;
  candidatesSearchValue: string;
  setCandidatesSearchValue: (value: string) => void;
  activeStatusTab: StatusTab;
  setActiveStatusTab: (status: StatusTab) => void;
  columnFilters: ColumnFilters;
  setColumnFilters: (filters: ColumnFilters) => void;
  localFilters: ColumnFilters;
  setLocalFilters: (filters: ColumnFilters) => void;
  columnFiltersOpen: boolean;
  setColumnFiltersOpen: (open: boolean) => void;
  sortDropdownOpen: boolean;
  setSortDropdownOpen: (open: boolean) => void;
  sortFieldsOrder: SortField[];
  setSortFieldsOrder: (sort: SortField[]) => void;
  candidatesCurrentPage: number;
  setCandidatesCurrentPage: (page: number) => void;
  candidatesTotalPages: number;
  pagedCandidates: InnerInterview[];
  handleCandidateSelect: (candidateId: string) => void;
  handleSelectAllCandidates: () => void;
  handleCandidateClick: (candidate: any) => void;
  areAllCandidatesSelected: boolean;
  draggedItem: number | null;
  setDraggedItem: (item: number | null) => void;
  handleDragStart: (index: number) => void;
  handleDragOver: (e: React.DragEvent, index: number) => void;
  handleDragEnd: () => void;
}

export function CandidatesTable({
  candidates,
  selectedPositionId,
  candidatesSearchValue,
  setCandidatesSearchValue,
  activeStatusTab,
  setActiveStatusTab,
  columnFilters,
  setColumnFilters,
  localFilters,
  setLocalFilters,
  columnFiltersOpen,
  setColumnFiltersOpen,
  sortDropdownOpen,
  setSortDropdownOpen,
  sortFieldsOrder,
  setSortFieldsOrder,
  candidatesCurrentPage,
  setCandidatesCurrentPage,
  candidatesTotalPages,
  pagedCandidates,
  handleCandidateSelect,
  handleSelectAllCandidates,
  handleCandidateClick,
  areAllCandidatesSelected,
  draggedItem,
  setDraggedItem,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
}: CandidatesTableProps) {
  const [isPdfLoading, setIsPdfLoading] = React.useState(false);
  const formatRating = (rating: number | null | undefined) => {
    if (rating === null || rating === undefined || rating === 0) return "—";
    return rating.toFixed(1);
  };
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((columnFiltersOpen || sortDropdownOpen) && event.target instanceof Element) {
        const filtersDropdown = event.target.closest('[data-dropdown="column-filters"]');
        const sortDropdown = event.target.closest('[data-dropdown="sort"]');

        if (columnFiltersOpen && !filtersDropdown) {
          setColumnFiltersOpen(false);
        }
        if (sortDropdownOpen && !sortDropdown) {
          setSortDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnFiltersOpen, sortDropdownOpen, setColumnFiltersOpen, setSortDropdownOpen]);

  const colors = useBrandingColors();
  const candidatesSearchInputRef = React.useRef<HTMLInputElement | null>(null);

  return (
    <div className="box-border content-stretch flex flex-col gap-4 items-start justify-between p-0 relative flex-1 w-full overflow-hidden">
      {/* Table and Pagination Container */}
      <div className="flex flex-col flex-1 w-full overflow-hidden">
        {/* Candidates Table with White Background */}
        <div className="bg-[#ffffff] relative rounded-2xl w-full flex flex-col flex-1 overflow-hidden">
          <div className="flex flex-col items-end overflow-clip relative flex-1">
            <div className="box-border content-stretch flex flex-col gap-[9px] items-end justify-start p-[20px] relative w-full flex-1 overflow-y-auto">
            {/* Filter Bar */}
            <div className="box-border content-stretch flex flex-row gap-4 items-center justify-between p-0 relative shrink-0 w-full">
              {/* Status Filter - Left Side */}
              <div className="bg-[#f6f8fa] relative rounded-lg shrink-0 w-fit">
                <div className="overflow-clip relative size-full">
                  <div className="box-border content-stretch flex flex-row gap-1 items-start justify-start p-[4px] relative w-full">
                    <button
                      onClick={() =>
                        setActiveStatusTab(
                          "all",
                        )
                      }
                      className={`relative rounded-lg shrink-0 px-3 py-[5px] cursor-pointer transition-all duration-200 ${
                        activeStatusTab ===
                        "all"
                          ? "bg-[#ffffff] shadow-[0px_6px_10px_0px_rgba(27,28,29,0.06),0px_2px_4px_0px_rgba(27,28,29,0.02)]"
                          : "bg-transparent"
                      }`}
                    >
                      <div className="flex flex-row items-center justify-center overflow-clip relative">
                        <div
                          className={`font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-left text-nowrap tracking-[-0.084px] ${
                            activeStatusTab ===
                            "all"
                              ? "text-[#0a0d14]"
                              : "text-[#868c98]"
                          }`}
                        >
                          <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                            Все
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() =>
                        setActiveStatusTab(
                          "successful",
                        )
                      }
                      className={`relative rounded-lg shrink-0 px-2 py-1 cursor-pointer transition-all duration-200 ${
                        activeStatusTab ===
                        "successful"
                          ? "bg-[#ffffff] shadow-[0px_6px_10px_0px_rgba(27,28,29,0.06),0px_2px_4px_0px_rgba(27,28,29,0.02)]"
                          : "bg-transparent"
                      }`}
                    >
                      <div className="flex flex-row items-center justify-center overflow-clip relative">
                        <div
                          className={`font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-left text-nowrap tracking-[-0.084px] ${
                            activeStatusTab ===
                            "successful"
                              ? "text-[#0a0d14]"
                              : "text-[#868c98]"
                          }`}
                        >
                          <p className="block leading-[20px] whitespace-pre">
                            Успешные
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() =>
                        setActiveStatusTab(
                          "failed",
                        )
                      }
                      className={`relative rounded-lg shrink-0 px-2 py-1 cursor-pointer transition-all duration-200 ${
                        activeStatusTab ===
                        "failed"
                          ? "bg-[#ffffff] shadow-[0px_6px_10px_0px_rgba(27,28,29,0.06),0px_2px_4px_0px_rgba(27,28,29,0.02)]"
                          : "bg-transparent"
                      }`}
                    >
                      <div className="flex flex-row items-center justify-center overflow-clip relative">
                        <div
                          className={`font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-left text-nowrap tracking-[-0.084px] ${
                            activeStatusTab ===
                            "failed"
                              ? "text-[#0a0d14]"
                              : "text-[#868c98]"
                          }`}
                        >
                          <p className="block leading-[20px] whitespace-pre">
                            Провал
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() =>
                        setActiveStatusTab(
                          "finished",
                        )
                      }
                      className={`relative rounded-lg shrink-0 px-2 py-1 cursor-pointer transition-all duration-200 ${
                        activeStatusTab ===
                        "finished"
                          ? "bg-[#ffffff] shadow-[0px_6px_10px_0px_rgba(27,28,29,0.06),0px_2px_4px_0px_rgba(27,28,29,0.02)]"
                          : "bg-transparent"
                      }`}
                    >
                      <div className="flex flex-row items-center justify-center overflow-clip relative">
                        <div
                          className={`font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-left text-nowrap tracking-[-0.084px] ${
                            activeStatusTab ===
                            "finished"
                              ? "text-[#0a0d14]"
                              : "text-[#868c98]"
                          }`}
                        >
                          <p className="block leading-[20px] whitespace-pre">
                            Завершённые
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Side Filters - Stick to Right */}
              <div className="box-border content-stretch flex flex-row gap-3 items-center justify-end p-0 relative shrink-0">
                {/* Search Bar */}
                <div className="relative rounded-md shrink-0 w-[400px] cursor-text" onClick={() => candidatesSearchInputRef.current?.focus()}>
                  <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start overflow-clip p-2 relative w-full rounded-md">
                    <Search2LineIconV7 />
                    <input
                      type="text"
                      placeholder="Поиск"
                      className="flex-1 css-z8d4os font-inter font-normal leading-[0] min-h-px min-w-px not-italic relative text-[#000000] text-[14px] text-left tracking-[-0.084px] bg-transparent border-none outline-none placeholder:text-[#868c98]"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      name="candidates-search"
                      value={candidatesSearchValue}
                      onChange={(e) => {
                        const searchTerm = e.target.value;
                        console.log("🔍 Candidates search term:", searchTerm);
                        setCandidatesSearchValue(searchTerm);
                      }}
                      ref={candidatesSearchInputRef}
                    />
                  </div>
                  <div
                    aria-hidden="true"
                    className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-md shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]"
                  />
                </div>

                <div className="relative" data-dropdown="column-filters">
                  <button
                    onClick={() => {
                      setColumnFiltersOpen(!columnFiltersOpen);
                      console.log(
                        "Filter clicked",
                      );
                    }}
                    className="bg-[#ffffff] relative rounded-md px-2 shrink-0 cursor-pointer hover:bg-[#f6f8fa] transition-colors duration-200"
                  >
                    <div className="box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-2 relative">
                      <Filter3LineIcon />
                      <div className="css-2x1u1o font-inter font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-center text-nowrap">
                        <p className="block leading-[20px] whitespace-pre">
                          Фильтр
                        </p>
                      </div>
                    </div>
                    <div
                      aria-hidden="true"
                      className={`absolute border border-solid inset-0 pointer-events-none rounded-md shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)] ${
                        columnFiltersOpen
                          ? "border-[#e16349]"
                          : "border-[#e2e4e9]"
                      }`}
                    />
                  </button>
                  <ColumnFiltersDropdown
                    isOpen={columnFiltersOpen}
                    localFilters={localFilters}
                    onLocalFiltersChange={setLocalFilters}
                    onApplyFilters={setColumnFilters}
                    onClose={() => setColumnFiltersOpen(false)}
                  />
                </div>

                <div className="relative" data-dropdown="sort">
                  <button
                    onClick={() => {
                      setSortDropdownOpen(!sortDropdownOpen);
                      console.log(
                        "Sort clicked",
                      );
                    }}
                    className="bg-[#ffffff] relative rounded-md shrink-0 w-fit cursor-pointer hover:bg-[#f6f8fa] transition-colors duration-200"
                  >
                  <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start overflow-clip p-2 relative">
                    <SortDescIcon />
                    <div className="css-2x1u1o font-inter font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap">
                      <p className="block leading-[20px] whitespace-pre">
                        Сортировать
                      </p>
                    </div>
                    <ArrowDownSLineIconV7
                      className={`w-5 h-5 transition-transform duration-200 ${
                        sortDropdownOpen
                          ? "rotate-180"
                          : ""
                      }`}
                    />
                  </div>
                  <div
                    aria-hidden="true"
                    className={`absolute border border-solid inset-0 pointer-events-none rounded-md shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)] ${
                      sortDropdownOpen
                        ? "border-[#e16349]"
                        : "border-[#e2e4e9]"
                    }`}
                  />
                </button>
                <SortDropdown
                  isOpen={sortDropdownOpen}
                  sortFieldsOrder={sortFieldsOrder}
                  onSortFieldsChange={setSortFieldsOrder}
                  onClose={() => setSortDropdownOpen(false)}
                  draggedItem={draggedItem}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                />
              </div>
              </div>
            </div>

            {/* Table Header */}
            <div className="bg-[#f6f8fa] box-border content-stretch flex flex-row items-start justify-start overflow-clip p-0 py-2 relative rounded-lg shrink-0 w-full">
              <div className="basis-0 bg-[#f6f8fa] grow min-h-px min-w-px relative shrink-0">
                <div className="flex flex-row items-center overflow-clip relative size-full">
                  <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start px-2 py-0 relative w-full">
                      <Checkbox
                        checked={areAllCandidatesSelected}
                        onChange={handleSelectAllCandidates}
                      />
                      <div className="basis-0 box-border content-stretch flex flex-row gap-0.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                      <div className="css-2x1u1o font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.084px]">
                        <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                          Имя
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="basis-0 bg-[#f6f8fa] grow min-h-px min-w-px relative shrink-0">
                <div className="flex flex-row items-center overflow-clip relative size-full">
                  <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start px-2 py-0 relative w-full">
                    <div className="basis-0 box-border content-stretch flex flex-row gap-0.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                      <div className="css-2x1u1o font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.084px]">
                        <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                          Оценка
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="basis-0 bg-[#f6f8fa] grow min-h-px min-w-px relative shrink-0">
                <div className="flex flex-row items-center overflow-clip relative size-full">
                  <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start px-2 py-0 relative w-full">
                    <div className="basis-0 box-border content-stretch flex flex-row gap-0.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                      <div className="css-2x1u1o font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.084px]">
                        <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                          Статус
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Result Header */}
              <div className="basis-0 bg-[#f6f8fa] grow min-h-px min-w-px relative shrink-0">
                <div className="flex flex-row items-center overflow-clip relative size-full">
                  <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start px-2 py-0 relative w-full">
                    <div className="basis-0 box-border content-stretch flex flex-row gap-0.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                      <div className="css-2x1u1o font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.084px]">
                        <p className="block leading-[20px] whitespace-pre">
                          Результат
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="basis-0 bg-[#f6f8fa] grow min-h-px min-w-px relative shrink-0">
                <div className="flex flex-row items-center overflow-clip relative size-full">
                  <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start px-2 py-0 relative w-full">
                    <div className="basis-0 box-border content-stretch flex flex-row gap-0.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
                      <div className="css-2x1u1o font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.084px]">
                        <p className="block leading-[20px] whitespace-pre">
                          Завершено
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table Content */}
            <div className="box-border content-stretch flex flex-col gap-0.5 items-start justify-start p-0 relative shrink-0 w-full">
              {pagedCandidates.map(
                (candidate, index) => (
                  <React.Fragment
                    key={candidate.id}
                  >
                    <div className="bg-[#ffffff] box-border content-stretch flex flex-row items-center justify-start py-[12px] relative rounded-xl shrink-0 w-full">
                      {/* Name Column */}
                      <div className="basis-0 bg-[#ffffff] grow min-h-px min-w-px relative shrink-0">
                        <div className="flex flex-row items-center overflow-clip relative size-full">
                          <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start pl-2 pr-4 py-0 relative w-full">
                              <Checkbox
                                checked={
                                  candidate.selected
                                }
                                onChange={() =>
                                  handleCandidateSelect(
                                    candidate.id,
                                  )
                                }
                              />
                              <div className="basis-0 box-border content-stretch flex flex-col gap-1 grow items-start justify-center min-h-px min-w-px not-italic p-0 relative shrink-0 text-left text-nowrap">
                              <div
                                className="css-vov616 font-['Inter:Medium',_sans-serif] font-medium overflow-ellipsis overflow-hidden relative shrink-0 text-[#0a0d14] text-[14px] tracking-[-0.084px] w-full cursor-pointer transition-colors"
                                style={{ color: '#0a0d14' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = colors.primary; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#0a0d14'; }}
                                onClick={() =>
                                  handleCandidateClick(candidate)
                                }
                              >
                                <p className="[text-overflow:inherit] [text-wrap-mode:inherit]' [white-space-collapse:inherit] block leading-[20px] overflow-inherit">
                                  {
                                    candidate.name
                                  }
                                </p>
                              </div>
                              <div className="css-u1gx4j font-['Inter:Regular',_sans-serif] font-normal overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[12px] w-full">
                                <p className="[text-overflow:inherit] [text-wrap-mode:inherit]' [white-space-collapse:inherit] block leading-[16px] overflow-inherit">
                                  {
                                    candidate.email || '—'
                                  }
                                </p>
                              </div>
                              {candidate.priorCommunication && (
                                <PriorCommunicationNotice info={candidate.priorCommunication} />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Rating Column */}
                      <div className="basis-0 bg-[#ffffff] grow min-h-px min-w-px relative shrink-0">
                        <div className="flex flex-row items-center overflow-clip relative size-full">
                          <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start pl-2 pr-4 py-0 relative w-full">
                            <div className="basis-0 box-border content-stretch flex flex-col gap-1 grow items-start justify-center min-h-px min-w-px p-0 relative shrink-0">
                              <div className="css-vov616 font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#0a0d14] text-[14px] text-left text-nowrap tracking-[-0.084px] w-full">
                                <p className="[text-overflow:inherit] [text-wrap-mode:inherit]' [white-space-collapse:inherit] block leading-[20px] overflow-inherit">
                                  {
                                    formatRating(candidate.rating as any)
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Column */}
                      <div className="basis-0 grow min-h-px min-w-px relative shrink-0">
                        <div className="flex flex-col justify-center relative size-full">
                          <div className="box-border content-stretch flex flex-col gap-2 items-start justify-center p-3 relative w-full">
                            <div className="css-vov616 font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#0a0d14] text-[14px] text-left text-nowrap tracking-[-0.084px] w-full">
                              <p className="[text-overflow:inherit] [text-wrap-mode:inherit]' [white-space-collapse:inherit] block leading-[20px] overflow-inherit">
                                {((candidate as any).interviewStatus === InterviewStatusEnum.NOT_STARTED && 'Не начато')
                                  || ((candidate as any).interviewStatus === InterviewStatusEnum.IN_PROGRESS && 'В процессе')
                                  || ((candidate as any).interviewStatus === InterviewStatusEnum.FINISHED && 'Завершено')
                                  || ((candidate as any).interviewStatus === InterviewStatusEnum.LIMIT_EXHAUSTED && 'Недоступно')
                                  || ((candidate as any).interviewStatus === 'CANCELLED' && 'Отменено')
                                  || '—'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Result Column */}
                      <div className="basis-0 bg-[#ffffff] grow min-h-px min-w-px relative shrink-0">
                        <div className="flex flex-row items-center overflow-clip relative size-full">
                          <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start pl-2 pr-4 py-0 relative w-full">
                            <div className="basis-0 box-border content-stretch flex flex-col gap-1 grow items-start justify-center min-h-px min-w-px p-0 relative shrink-0">
                              <div className="css-vov616 font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#0a0d14] text-[14px] text-left text-nowrap tracking-[-0.084px] w-full">
                                <p className="[text-overflow:inherit] [text-wrap-mode:inherit]' [white-space-collapse:inherit] block leading-[20px] overflow-inherit">
                                  {((candidate as any).interviewStatus === InterviewStatusEnum.FINISHED && (candidate as any).result === InterviewResultEnum.SUCCESSFUL && 'Успешно') || 
                                   ((candidate as any).interviewStatus === InterviewStatusEnum.FINISHED && (candidate as any).result === InterviewResultEnum.UNSUCCESSFUL && 'Провал') || 
                                   ((candidate as any).interviewStatus === InterviewStatusEnum.FINISHED && (candidate as any).result === InterviewResultEnum.ERROR && 'Ошибка') || 
                                   '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Date Column */}
                      <div className="basis-0 bg-[#ffffff] grow min-h-px min-w-px relative shrink-0">
                        <div className="flex flex-row items-center overflow-clip relative size-full">
                          <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start pl-2 pr-4 py-0 relative w-full">
                            <div className="basis-0 box-border content-stretch flex flex-col gap-1 grow items-start justify-center min-h-px min-w-px p-0 relative shrink-0">
                              <div className="css-vov616 font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#0a0d14] text-[14px] text-left text-nowrap tracking-[-0.084px] w-full">
                                <p className="[text-overflow:inherit] [text-wrap-mode:inherit]' [white-space-collapse:inherit] block leading-[20px] overflow-inherit">
                                  {
                                    candidate.completed
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Divider between rows */}
                    {index <
                      pagedCandidates.length -
                        1 && (
                      <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-0 py-0 relative shrink-0 w-full">
                        <div className="basis-0 bg-[#e2e4e9] grow h-px min-h-px min-w-px shrink-0" />
                      </div>
                    )}
                  </React.Fragment>
                ),
              )}
            </div>

          </div>
        </div>
        </div>

        {/* Pagination for candidates - outside white card */}
        {candidatesTotalPages > 1 && (
          <div className="flex justify-center w-full shrink-0 mt-3">
            <Pagination
              currentPage={candidatesCurrentPage}
              totalPages={candidatesTotalPages}
              onPageChange={setCandidatesCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
